import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
  UserRoundCog,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const Login = () => {
  const [type, setType] = useState("Client");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(false);

  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    setMessage("");
    setAccessCode("");
  }, [type]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          type,
          accessCode: type === "Admin" ? accessCode : "",
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessage(
          data.message
            ? `${t?.errorLabel || "Erreur"}: ${data.message}`
            : t?.serverError || "Connexion impossible."
        );
        setMessageType("error");

        if (data.redirect) {
          window.setTimeout(() => {
            window.location.href = data.redirect;
          }, 700);
        }
        return;
      }

      if (!data.token) {
        throw new Error("Token absent de la réponse du serveur.");
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user || {}));
      setMessage(t?.loginSuccess || "Connexion réussie.");
      setMessageType("success");
      window.dispatchEvent(new Event("loginStateChanged"));

      window.setTimeout(() => {
        // Redirection conservée vers l'accueil après connexion.
        navigate("/", { replace: true });
      }, 650);
    } catch (error) {
      console.error("Login error:", error);
      setMessage(error.message || t?.serverError || "Erreur serveur.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto grid min-h-[calc(100vh-9rem)] max-w-6xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-600/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px)] [background-size:40px_40px]" />

          <div className="relative">
            <Link to="/" className="inline-flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg">
                <img
                  src="/logo.jpeg"
                  alt="CertiLink"
                  className="h-full w-full object-contain p-1"
                />
              </span>
              <span className="text-xl font-black">CertiLink</span>
            </Link>

            <div className="mt-20 max-w-lg">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-xs font-bold text-cyan-200 ring-1 ring-white/10">
                <Sparkles className="h-4 w-4" />
                Espace sécurisé
              </div>
              <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight xl:text-5xl">
                Gérez la certification depuis un espace clair et professionnel.
              </h1>
              <p className="mt-5 text-base leading-8 text-slate-300">
                Connectez-vous selon votre rôle pour accéder uniquement aux outils
                et aux données qui vous sont autorisés.
              </p>
            </div>
          </div>

          <div className="relative grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {[
              [ShieldCheck, "Accès par rôle"],
              [BadgeCheck, "Données vérifiables"],
              [LockKeyhole, "Session sécurisée"],
            ].map(([Icon, label]) => (
              <div
                key={label}
                className="rounded-2xl border border-white/10 bg-white/5 p-4"
              >
                <Icon className="h-5 w-5 text-cyan-300" />
                <p className="mt-3 text-sm font-bold text-slate-100">{label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="flex items-center p-6 sm:p-10 lg:p-12">
          <div className="mx-auto w-full max-w-md">
            <div className="mb-8 lg:hidden">
              <Link to="/" className="inline-flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <img
                    src="/logo.jpeg"
                    alt="CertiLink"
                    className="h-full w-full object-contain p-1"
                  />
                </span>
                <span className="text-lg font-black text-slate-950">CertiLink</span>
              </Link>
            </div>

            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
              Authentification
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">
              {t?.loginTitle || "Connexion"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {t?.signIn || "Connectez-vous à votre espace CertiLink."}
            </p>

            <div className="mt-7 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1.5">
              {[
                ["Client", Building2, t?.clientRole || "Client"],
                ["Admin", UserRoundCog, t?.adminRole || "Administrateur"],
              ].map(([value, Icon, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setType(value)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-black transition ${
                    type === value
                      ? "bg-white text-blue-700 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {message && (
              <div
                className={`mt-6 flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${
                  messageType === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`}
              >
                {messageType === "success" ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                )}
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              <label className="block text-sm font-bold text-slate-700">
                {t?.emailLabel || "Adresse email"}
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t?.emailPlaceholder || "email@example.com"}
                    required
                    autoComplete="email"
                    className={inputClass}
                  />
                </div>
              </label>

              <label className="block text-sm font-bold text-slate-700">
                {t?.passwordLabel || "Mot de passe"}
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t?.passwordPlaceholder || "Votre mot de passe"}
                    required
                    autoComplete="current-password"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </label>

              {type === "Admin" && (
                <label className="block text-sm font-bold text-slate-700">
                  {t?.ui?.login?.adminAccessCode || "Code d’accès administrateur"}
                  <div className="relative mt-2">
                    <ShieldCheck className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showAccessCode ? "text" : "password"}
                      value={accessCode}
                      onChange={(event) => setAccessCode(event.target.value)}
                      placeholder={t?.ui?.login?.adminAccessCodePlaceholder || "Votre code d’accès personnel"}
                      autoComplete="off"
                      required
                      className={inputClass}
                    />
                    <button
                      type="button"
                      aria-label={showAccessCode ? "Masquer le code" : "Afficher le code"}
                      onClick={() => setShowAccessCode((current) => !current)}
                      className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    >
                      {showAccessCode ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <span className="mt-2 block text-xs font-semibold text-slate-500">
                    {t?.ui?.login?.adminAccessCodeHelp || "Chaque administrateur utilise son propre code d’accès en plus de son mot de passe."}
                  </span>
                </label>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ArrowRight className="h-5 w-5" />
                )}
                {loading ? "Connexion..." : t?.loginButton || "Se connecter"}
              </button>
            </form>

            <div className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className="text-sm text-slate-600">
                Vous n’avez pas encore de compte ?
              </p>
              <Link
                to="/requests/create"
                className="mt-2 inline-flex items-center gap-2 text-sm font-black text-blue-700 hover:text-blue-900"
              >
                {t?.requestAccess || "Demander un accès"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Login;
